import { useCallback, useEffect, useRef, useState } from 'react';
import { StaleClientError, type FleetCommandView } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';

const POLL_INTERVAL_MS = 5_000;
/**
 * While a command is in flight (OPS-57). A host posts each step the moment it
 * happens — received and running a second or two after the press — and the
 * page has no live channel, so a 5s poll would skip straight past them.
 */
const IN_FLIGHT_POLL_INTERVAL_MS = 1_000;

type Options = {
  /** Suppresses the interval while a mutation is in flight (BL-108) — resumed on the 202, not the outcome. */
  readonly paused?: boolean;
};

type Result = {
  readonly commands: ReadonlyArray<FleetCommandView>;
  /** True only for the first load of the currently selected machine (BL-011) — a poll never re-enters it. */
  readonly loading: boolean;
  readonly refresh: () => Promise<void>;
};

/**
 * One poll of `GET …/commands` for the selected machine — every 5s, every 1s
 * while any command is unsettled so its steps show as they land (OPS-57) — feeding both the
 * activity feed and every open log viewer (IMM-910.8 Decisions — one poll,
 * two consumers). Suppressed while the tab is hidden, a mutation is in
 * flight, or a fetch is already in flight; a response for a machine that is
 * no longer selected is discarded (BL-096). `machineId: null` — no machine
 * selected (e.g. an empty fleet) — never fetches.
 */
export function useFleetCommands(machineId: string | null, options?: Options): Result {
  const [commands, setCommands] = useState<ReadonlyArray<FleetCommandView>>([]);
  const [loading, setLoading] = useState(machineId !== null);
  const machineIdRef = useRef(machineId);
  const inFlightRef = useRef(false);
  const loadedForRef = useRef<string | null>(null);
  const [staleError, setStaleError] = useState<StaleClientError | null>(null);
  const pausedRef = useRef(options?.paused ?? false);
  pausedRef.current = options?.paused ?? false;

  const load = useCallback(async (loadOptions?: { readonly force?: boolean }) => {
    const id = machineIdRef.current;

    if (id === null) return;

    if (inFlightRef.current && loadOptions?.force !== true) return;
    inFlightRef.current = true;

    try {
      const result = await apiClient.adminFleetCommands.list({ pathParams: { machineId: id } });

      // The selection moved on while this request was in flight — a slow
      // response for the previous machine must not overwrite the current one.
      if (machineIdRef.current !== id) return;

      if (!result.ok) return;

      setCommands(result.body.data);
      setLoading(false);
      loadedForRef.current = id;
    } catch (err) {
      // A poll fires from a timer, so a rejection here reaches no error
      // boundary — it would only be an unhandled rejection, repeated every
      // tick, while the stale tab never shows its reload offer. Rethrow it
      // from render so the route's `errorElement` renders the outdated view.
      if (err instanceof StaleClientError) {
        setStaleError(err);

        return;
      }

      throw err;
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    machineIdRef.current = machineId;

    if (machineId === null) {
      setCommands([]);
      setLoading(false);
      loadedForRef.current = null;

      return;
    }

    if (loadedForRef.current !== machineId) {
      setLoading(true);
      setCommands([]);
    }

    void load({ force: true });
  }, [machineId, load]);

  const inFlight = commands.some((command) => command.outcome === 'accepted');

  useEffect(() => {
    const interval = window.setInterval(
      () => {
        if (pausedRef.current) return;

        if (document.visibilityState !== 'visible') return;

        void load();
      },
      inFlight ? IN_FLIGHT_POLL_INTERVAL_MS : POLL_INTERVAL_MS,
    );

    return () => window.clearInterval(interval);
  }, [load, inFlight]);

  const refresh = useCallback(() => load({ force: true }), [load]);

  if (staleError !== null) throw staleError;

  return { commands, loading, refresh };
}
